import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { formatMapName } from '../../liveSession/lib/formatters';
import { useMyTeam } from '../../team/useMyTeam';
import { STRATOS_MAPS, UTILITY_TYPES } from '../lib/authoring';
import {
  createUtilityUse,
  deleteUtilityUse,
  listUtilityUses,
  updateUtilityUse,
} from '../strategyApi';
import type { UtilityUseRecord } from '../types';
import PlaybookShell from './PlaybookShell';
import styles from './author.module.css';

type UsedFilter = 'all' | 'used' | 'unused';

type LineupDraft = {
  name: string;
  map: string;
  utilityType: string;
  description: string;
  url: string;
};

export default function LineupsPage() {
  const { user } = useAuth();
  const { team, loading, error } = useMyTeam(user?.id);

  return (
    <PlaybookShell crumb="Lineups">
      {loading && <p className={styles.status}>Loading your team…</p>}
      {!loading && error && <p className={styles.error}>{error.message}</p>}
      {!loading && !error && !team && <p className={styles.status}>Join a team to see its lineups.</p>}
      {team && <Library teamId={team.id} canEdit={team.role === 'strategist'} />}
    </PlaybookShell>
  );
}

function Library({ teamId, canEdit }: { teamId: string; canEdit: boolean }) {
  const [rows, setRows] = useState<UtilityUseRecord[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<LineupDraft | null>(null);
  const [query, setQuery] = useState('');
  const [mapFilter, setMapFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [usedFilter, setUsedFilter] = useState<UsedFilter>('all');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const editorRef = useRef<HTMLElement>(null);

  function reload() {
    return listUtilityUses(teamId).then(setRows);
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listUtilityUses(teamId)
      .then((list) => {
        if (!cancelled) setRows(list);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load lineups');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (mapFilter !== 'all' && row.map.replace(/^de_/i, '') !== mapFilter.replace(/^de_/i, '')) return false;
      if (typeFilter !== 'all' && row.utilityType !== typeFilter) return false;
      if (usedFilter === 'used' && row.usedBy.length === 0) return false;
      if (usedFilter === 'unused' && row.usedBy.length > 0) return false;
      const blob = `${row.name} ${row.description ?? ''} ${formatMapName(row.map)}`.toLowerCase();
      if (needle && !blob.includes(needle)) return false;
      return true;
    });
  }, [rows, mapFilter, typeFilter, usedFilter, query]);

  const selected = visible.find((row) => row.id === selectedId) ?? visible[0] ?? null;
  const editing = Boolean(draft && editingId && selected?.id === editingId);
  const usedCount = rows.filter((row) => row.usedBy.length > 0).length;
  const filtersOn = query.trim() !== '' || mapFilter !== 'all' || typeFilter !== 'all' || usedFilter !== 'all';

  useEffect(() => {
    if (!editingId || selected?.id === editingId) return;
    setEditingId(null);
    setDraft(null);
    setConfirming(false);
  }, [editingId, selected?.id]);

  function show(id: string) {
    setSelectedId(id);
    setEditingId(null);
    setDraft(null);
    setConfirming(false);
    editorRef.current?.scrollIntoView?.({ block: 'nearest' });
  }

  function openEdit(row: UtilityUseRecord) {
    setSelectedId(row.id);
    setConfirming(false);
    if (editingId !== row.id) {
      setEditingId(row.id);
      setDraft(toDraft(row));
    }
    editorRef.current?.scrollIntoView?.({ block: 'nearest' });
  }

  function cancelEdit() {
    setEditingId(null);
    setDraft(null);
    setConfirming(false);
  }

  async function createNew() {
    setError(null);
    try {
      const created = await createUtilityUse(teamId, {
        map: mapFilter === 'all' ? 'de_mirage' : mapFilter,
        name: 'New lineup',
        utilityType: typeFilter === 'all' ? 'smoke' : typeFilter,
        description: '',
        url: '',
      });
      const row = { ...created, usedBy: created.usedBy ?? [] };
      setRows((current) => [row, ...current.filter((item) => item.id !== row.id)]);
      setSelectedId(row.id);
      setEditingId(row.id);
      setDraft(toDraft(row));
      setConfirming(false);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create a lineup');
    }
  }

  async function saveDraft(event: FormEvent) {
    event.preventDefault();
    if (!draft || !editingId || saving) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await updateUtilityUse(teamId, editingId, {
        map: draft.map,
        name: draft.name,
        description: draft.description,
        url: draft.url,
        utilityType: draft.utilityType,
      });
      setRows((current) => current.map((row) => (
        row.id === saved.id ? { ...row, ...saved, usedBy: saved.usedBy ?? row.usedBy } : row
      )));
      setEditingId(null);
      setDraft(null);
      setConfirming(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the lineup');
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!selected) return;
    setError(null);
    try {
      await deleteUtilityUse(teamId, selected.id);
      setConfirming(false);
      setEditingId(null);
      setDraft(null);
      setSelectedId(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the lineup');
    }
  }

  return (
    <div className={styles.studio}>
      <div className={styles.head}>
        <div>
          <div className={styles.eyebrow}>Shared throws</div>
          <h1>Lineups</h1>
          <p>A throw lives once. Any strategy on that map can use it, instead of writing it again.</p>
        </div>
        {canEdit && (
          <button type="button" className={styles.primary} onClick={() => void createNew()}>New lineup</button>
        )}
      </div>

      <section className={styles.stats} aria-label="Lineup counts">
        <div className={styles.stat}>
          <div>
            <div className={styles.micro}>In the library</div>
            <p>Across every map</p>
          </div>
          <strong>{rows.length}</strong>
        </div>
        <div className={`${styles.stat} ${styles.known}`}>
          <div>
            <div className={styles.micro}>In use</div>
            <p>Pointed at by a strategy</p>
          </div>
          <strong>{usedCount}</strong>
        </div>
        <div className={`${styles.stat} ${styles.pending}`}>
          <div>
            <div className={styles.micro}>Not used yet</div>
            <p>Ready when a call needs them</p>
          </div>
          <strong>{rows.length - usedCount}</strong>
        </div>
      </section>

      {error && <p className={styles.error}>{error}</p>}

      <div className={styles.library}>
        <section className={styles.studioPanel} aria-label="Lineup library">
          <div className={styles.filterArea}>
            <div className={styles.filterTop}>
              <input
                className={styles.search}
                type="search"
                aria-label="Search lineups"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by throw or note…"
                autoComplete="off"
              />
              <div className={styles.useFilter} role="group" aria-label="Lineup use">
                {(['all', 'used', 'unused'] as const).map((value) => (
                  <button key={value} type="button" aria-pressed={usedFilter === value} onClick={() => setUsedFilter(value)}>
                    {value === 'all' ? 'All lineups' : value === 'used' ? 'In use' : 'Not used yet'}
                  </button>
                ))}
              </div>
            </div>
            <div className={styles.filterRow}>
              <label>
                Map
                <select aria-label="Map" value={mapFilter} onChange={(event) => setMapFilter(event.target.value)}>
                  <option value="all">All maps</option>
                  {STRATOS_MAPS.map((map) => <option key={map.id} value={map.id}>{map.name}</option>)}
                </select>
              </label>
              <label>
                Grenade
                <select aria-label="Grenade" value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
                  <option value="all">All grenades</option>
                  {UTILITY_TYPES.map((type) => <option key={type} value={type}>{utilityLabel(type)}</option>)}
                </select>
              </label>
              <button
                type="button"
                className={styles.reset}
                onClick={() => { setQuery(''); setMapFilter('all'); setTypeFilter('all'); setUsedFilter('all'); }}
                disabled={!filtersOn}
              >
                Reset filters
              </button>
            </div>
          </div>

          <div className={styles.resultsBar}>
            <span role="status">{visible.length} of {rows.length} lineups</span>
          </div>

          {loading && <p className={styles.studioMessage}>Loading lineups…</p>}
          {!loading && visible.length === 0 && (
            <div className={styles.studioEmpty}>
              <div className={styles.eyebrow}>No matching throws</div>
              <h2>No lineups found</h2>
              <p>Try a different search or broaden your filters.</p>
            </div>
          )}

          {!loading && visible.length > 0 && (
            <div className={styles.tableWrap}>
              <table>
                <thead>
                  <tr>
                    <th scope="col">Lineup</th>
                    <th scope="col">Map</th>
                    <th scope="col">Grenade</th>
                    <th scope="col">Used by</th>
                    {canEdit && <th scope="col"><span className={styles.micro}>Edit</span></th>}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row) => (
                    <tr
                      key={row.id}
                      className={selected?.id === row.id ? styles.selectedRow : undefined}
                      onClick={() => show(row.id)}
                    >
                      <td>
                        <div className={styles.strategyCell}>
                          <button
                            type="button"
                            className={styles.nameButton}
                            aria-pressed={selected?.id === row.id}
                            onClick={() => show(row.id)}
                          >
                            {row.name}
                          </button>
                        </div>
                        <p className={styles.rowDescription}>{row.description?.trim() || 'No throw notes yet.'}</p>
                      </td>
                      <td><div className={styles.mapName}>{formatMapName(row.map) || row.map}</div></td>
                      <td><span className={grenadeBadge(row.utilityType)}>{utilityLabel(row.utilityType)}</span></td>
                      <td>{row.usedBy.length === 0 ? '—' : `${row.usedBy.length} ${row.usedBy.length === 1 ? 'strategy' : 'strategies'}`}</td>
                      {canEdit && (
                        <td>
                          <button
                            type="button"
                            className={styles.edit}
                            aria-label={`Edit ${row.name}`}
                            aria-pressed={editingId === row.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              openEdit(row);
                            }}
                          >
                            <PenIcon />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {selected && (
          <aside className={styles.editorCard} ref={editorRef} aria-label="Lineup details">
            {editing && draft ? (
              <>
                <div className={styles.cardHead}>
                  <h2>Edit lineup</h2>
                  <p>Changing these notes updates every strategy that points here.</p>
                </div>
                <form className={styles.lineupEditor} onSubmit={(event) => void saveDraft(event)}>
                  <label>
                    Name
                    <input
                      aria-label="Lineup name"
                      value={draft.name}
                      onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                    />
                  </label>
                  <label>
                    Map
                    <select aria-label="Lineup map" value={draft.map} onChange={(event) => setDraft({ ...draft, map: event.target.value })}>
                      {STRATOS_MAPS.map((map) => <option key={map.id} value={map.id}>{map.name}</option>)}
                      {!STRATOS_MAPS.some((map) => map.id === draft.map) && <option value={draft.map}>{draft.map}</option>}
                    </select>
                  </label>
                  <label>
                    Grenade
                    <select aria-label="Lineup grenade" value={draft.utilityType} onChange={(event) => setDraft({ ...draft, utilityType: event.target.value })}>
                      {UTILITY_TYPES.map((type) => <option key={type} value={type}>{utilityLabel(type)}</option>)}
                    </select>
                  </label>
                  <label>
                    Throw notes
                    <textarea value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} />
                  </label>
                  <label>
                    Learning link
                    <input value={draft.url} placeholder="https://" onChange={(event) => setDraft({ ...draft, url: event.target.value })} />
                  </label>
                  <div className={styles.editActions}>
                    <button type="submit" className={styles.primary} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                    <button type="button" className={styles.secondary} onClick={cancelEdit} disabled={saving}>Cancel</button>
                  </div>
                  <UsedByList uses={selected.usedBy} canEdit={canEdit} />
                  {!confirming && (
                    <button type="button" className={styles.danger} onClick={() => setConfirming(true)}>Delete lineup</button>
                  )}
                  {confirming && (
                    <div className={styles.confirm}>
                      <p>
                        {selected.usedBy.length > 0
                          ? 'Deleting detaches this lineup from every action that uses it. Those actions stay.'
                          : 'Delete this lineup?'}
                      </p>
                      <button type="button" className={styles.danger} onClick={() => void remove()}>Delete</button>
                      <button type="button" onClick={() => setConfirming(false)}>Keep lineup</button>
                    </div>
                  )}
                </form>
              </>
            ) : (
              <>
                <div className={styles.cardHead}>
                  <div className={styles.cardTitleRow}>
                    <div>
                      <div className={styles.micro}>Lineup</div>
                      <h2 className={styles.viewName}>{selected.name}</h2>
                    </div>
                    {canEdit && (
                      <button type="button" className={styles.secondary} onClick={() => openEdit(selected)}>
                        <PenIcon />
                        Edit
                      </button>
                    )}
                  </div>
                  <div className={styles.viewMeta}>
                    <span>{formatMapName(selected.map) || selected.map}</span>
                    <span className={grenadeBadge(selected.utilityType)}>{utilityLabel(selected.utilityType)}</span>
                  </div>
                </div>
                <div className={styles.lineupView}>
                  <div>
                    <div className={styles.micro}>Throw notes</div>
                    <p className={styles.viewCopy}>{selected.description?.trim() || 'No throw notes yet.'}</p>
                  </div>
                  <div>
                    <div className={styles.micro}>Learning link</div>
                    {learningHref(selected.url) ? (
                      <a className={styles.viewLink} href={learningHref(selected.url) ?? undefined} target="_blank" rel="noreferrer">
                        {selected.url}
                      </a>
                    ) : (
                      <p className={styles.viewCopy}>No learning link yet.</p>
                    )}
                  </div>
                  <UsedByList uses={selected.usedBy} canEdit={canEdit} />
                </div>
              </>
            )}
          </aside>
        )}
      </div>
      <p className={styles.footerNote}>
        {canEdit
          ? 'Open a lineup to read it. Edit when the notes need to change, then save.'
          : 'These throws are shared. Every strategy that points at one uses the same notes.'}
      </p>
    </div>
  );
}

function UsedByList({ uses, canEdit }: { uses: UtilityUseRecord['usedBy']; canEdit: boolean }) {
  return (
    <div className={styles.usedBy}>
      <div className={styles.micro}>Used by</div>
      {uses.length === 0 && <p>No strategy uses this lineup.</p>}
      {uses.length > 0 && (
        <ul>
          {uses.map((use) => {
            const open = canEdit || use.status.trim().toLowerCase() === 'active';
            return (
              <li key={use.strategyId}>
                {open ? (
                  <Link to={`/strategies/${use.strategyId}`}>{use.strategyName}</Link>
                ) : (
                  <span className={styles.usedName}>{use.strategyName}</span>
                )}
                <span className={statusBadge(use.status)}>{statusLabel(use.status)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function toDraft(row: UtilityUseRecord): LineupDraft {
  return {
    name: row.name,
    map: row.map,
    utilityType: row.utilityType,
    description: row.description ?? '',
    url: row.url ?? '',
  };
}

function learningHref(url: string | null | undefined) {
  const value = url?.trim() ?? '';
  return /^https?:\/\//i.test(value) ? value : null;
}

function PenIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4z" />
    </svg>
  );
}

function utilityLabel(type: string) {
  if (type === 'he') return 'HE';
  return type ? type.charAt(0).toUpperCase() + type.slice(1) : type;
}

function grenadeBadge(type: string) {
  const tone = type === 'smoke' ? styles.smoke
    : type === 'flash' ? styles.flash
      : type === 'he' ? styles.he
        : type === 'molotov' ? styles.molotov
          : type === 'decoy' ? styles.decoy
            : undefined;
  return tone ? `${styles.chip} ${tone}` : styles.chip;
}

function statusLabel(status: string) {
  const key = status.trim().toLowerCase();
  if (key === 'active') return 'Active';
  if (key === 'inactive') return 'Inactive';
  if (key === 'draft') return 'Draft';
  return status;
}

function statusBadge(status: string) {
  const key = status.trim().toLowerCase();
  const tone = key === 'active' ? styles.active
    : key === 'inactive' ? styles.inactive
      : key === 'draft' ? styles.draft
        : undefined;
  return tone ? `${styles.chip} ${tone}` : styles.chip;
}
